import React, { useContext, useEffect, useRef } from 'react';
import { Button, Skeleton, useMantineTheme } from '@mantine/core';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, ShoppingCart } from 'tabler-icons-react';
import { Context as AuthContext } from '../../../providers/AuthProvider';
import { Context as RegistrationContext } from '../../../providers/RegistrationProvider';
import { Context as RegistrationAdminDashboardContext } from '../../../providers/RegistrationAdminDashboardProvider';
import sportsHeadzLogo from '../../../images/sportsheadz.png';
import { links } from './links';
import AppFlexbox from '../../common/AppFlexbox';
import AppLink from '../../common/AppLink';
import AppImage from '../../common/AppImage';
import ActionableIcon from '../../common/ActionableIcon';
import Drawer from './Drawer';
import { getRegAssociationAdminPermissions } from '../../../helpers/adminHelper';
import { REG_PERMISSION_ENUM } from '../../../config/constants';
import HeaderAccountNavigationMenu from './HeaderAccountNavigationMenu';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { useMediaQueries } from '../../../helpers/hooks';

const Header = () => {
  const theme = useMantineTheme();
  const { isMobileOrSmaller } = useMediaQueries();
  const hasFetchedAssociation = useRef(false);
  const { state } = useContext(AuthContext);
  const { state: regState } = useContext(RegistrationContext);
  const {
    state: helperState,
    triggerFormSubmit,
    triggerFormDiscard
  } = useContext(RegistrationAdminDashboardContext);
  const { pathname } = useLocation();
  let headerImage = sportsHeadzLogo;
  let lightMode = false;
  if (
    /(\/association\/)\d+/.test(pathname) &&
    !pathname.includes('branding') &&
    hasFetchedAssociation.current &&
    !regState.regAssociation.loading &&
    (!regState.regAssociation.value?.sandboxEnabled ||
      state.userData?.isRegistrationAdmin)
  ) {
    headerImage =
      regState.regAssociation.value?.logoImageUrl ?? sportsHeadzLogo;
    lightMode = regState.regAssociation.value?.lightModeTheme ?? false;
  }
  const textColor = lightMode ? '#000' : '#FFF';

  const adminPermissions = getRegAssociationAdminPermissions(
    state.regAssociationAdmins.value
  );
  const adminLinks = links.admin.filter(
    (link) =>
      adminPermissions.includes(REG_PERMISSION_ENUM.SUPER_ADMIN) ||
      link.permissions.some((p) => adminPermissions.includes(p))
  );
  const authLoading = !state.tokenAttempted || state.loading;

  const showFormUnsavedChanges =
    helperState.form.hasUnsavedChanges &&
    (!helperState.form.getIsVisible ||
      helperState.form.getIsVisible(helperState.form.data));

  useEffect(() => {
    if (regState.regAssociation.loading) {
      hasFetchedAssociation.current = true;
    }
  }, [regState.regAssociation.loading]);

  return (
    <AppFlexbox
      p={{ base: showFormUnsavedChanges ? 0 : '0 30px', sm: '0 30px' }}
      style={{
        flex: 1,
        height: '100%',
        borderColor: lightMode ? theme.colors.gray[5] : theme.colors.dark[7],
        boxShadow: `0 2px 2px 0 ${
          lightMode ? theme.colors.gray[5] : theme.colors.dark[7]
        }`,
        justifyContent: 'space-between',
        placeItems: 'center',
        backgroundColor: lightMode ? theme.white : theme.black
      }}
    >
      {showFormUnsavedChanges ? (
        <AppFlexbox
          style={{
            flex: 1,
            height: '100%',
            justifyContent: 'space-between',
            placeItems: 'center',
            gap: 5
          }}
        >
          <AppFlexbox
            maw={{ base: 'unset', xsm: 240 }}
            pl={{ base: 10, xsm: 15 }}
            style={{
              flex: 1,
              gap: 10
            }}
            visibleFrom={helperState.form.hasUnsavedChanges ? 'sm' : null}
          >
            <AppFlexbox component={Link} to="/">
              <AppImage
                alt="SportsHeadz Main Logo"
                fit="contain"
                height={80}
                src={headerImage}
                style={{ minWidth: 'unset', maxWidth: 125 }}
                width="100%"
              />
            </AppFlexbox>
          </AppFlexbox>

          <AppStack
            style={{
              flex: 1,
              margin: 'auto',
              width: '100%',
              padding: isMobileOrSmaller ? 16 : 0,
              maxWidth: helperState.form.containerWidth ?? 950
            }}
          >
            <AppFlexbox
              style={{
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <AppFlexbox
                style={{ alignItems: 'center', gap: 5 }}
                visibleFrom="xxs"
              >
                <AppFlexbox
                  style={{
                    width: 36,
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <AlertTriangle color="#dee2e6" />
                </AppFlexbox>
                <AppText
                  style={{
                    fontSize: 20,
                    fontWeight: 650,
                    color: '#dee2e6',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Unsaved changes
                </AppText>
              </AppFlexbox>

              <AppFlexbox
                style={{
                  width: '100%',
                  justifyContent: 'end',
                  gap: 10,
                  alignItems: 'center'
                }}
              >
                {!helperState.form.hideDiscardButton && (
                  <Button
                    color="dark"
                    disabled={helperState.form.isSubmitting}
                    flex={{ base: 1, xxs: 'unset' }}
                    onClick={triggerFormDiscard}
                    radius="md"
                    size="compact-lg"
                    style={{ fontSize: 14 }}
                    type="button"
                    variant="filled"
                  >
                    Discard
                  </Button>
                )}

                <Button
                  color="dark"
                  flex={{ base: 1, xxs: 'unset' }}
                  loading={helperState.form.isSubmitting}
                  onClick={() => triggerFormSubmit(true)}
                  radius="md"
                  size="compact-lg"
                  style={{ fontSize: 14 }}
                  type="button"
                  variant="white"
                >
                  {helperState.form.saveButtonText || 'Save'}
                </Button>
              </AppFlexbox>
            </AppFlexbox>
          </AppStack>
        </AppFlexbox>
      ) : (
        <>
          <AppFlexbox style={{ flex: 1, margin: 'auto 0' }} visibleFrom="sm">
            {links.public.map((link) => (
              <AppLink
                key={link.to}
                color={textColor}
                component={Link}
                isActive={link.isSelected(pathname)}
                label={link.title}
                to={link.to}
              />
            ))}
          </AppFlexbox>

          <AppFlexbox component={Link} to="/">
            <AppImage
              alt="SportsHeadz Main Logo"
              fit="contain"
              height={80}
              src={headerImage}
              style={{ minWidth: 'unset', maxWidth: 125 }}
              width="100%"
            />
          </AppFlexbox>

          <AppFlexbox
            style={{
              flex: 1,
              justifyContent: 'flex-end',
              placeItems: 'center',
              gap: 25
            }}
          >
            {state.isAuthenticated && (
              <ActionableIcon
                component={Link}
                indicatorValue={
                  !regState.regCart.value ||
                  regState.regCart.value.cart.lineItems.length === 0
                    ? null
                    : regState.regCart.value.cart.lineItems.reduce(
                        (total, item) => total + item.quantity,
                        0
                      )
                }
                to={state.isAuthenticated ? '/checkout/cart' : '/auth/login'}
              >
                <ShoppingCart color={textColor} size={30} />
              </ActionableIcon>
            )}
            <AppFlexbox visibleFrom="sm">
              {authLoading ? (
                <Skeleton
                  height={48}
                  radius="xs"
                  style={{ opacity: lightMode ? 1 : 0.2 }}
                  width={210}
                />
              ) : state.isAuthenticated ? (
                <HeaderAccountNavigationMenu
                  adminLinks={adminLinks}
                  lightMode={lightMode}
                />
              ) : (
                <Button
                  component={Link}
                  radius="xs"
                  size="md"
                  style={{
                    color: lightMode ? theme.black : theme.white,
                    borderColor: lightMode ? theme.colors.gray[4] : theme.white
                  }}
                  to="/auth/login"
                  variant="outline"
                >
                  Sign In
                </Button>
              )}
            </AppFlexbox>
            <AppFlexbox hiddenFrom="sm">
              <Drawer
                adminLinks={adminLinks}
                lightMode={lightMode}
                textColor={textColor}
              />
            </AppFlexbox>
          </AppFlexbox>
        </>
      )}
    </AppFlexbox>
  );
};

export default Header;
